// src/components/images/Images.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Grid, List } from 'lucide-react'; // Filter removed correctly
import apiService from '../../services/api';
import { exportToCsv } from '../../utils/helpers';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useAppContext } from '../../context/AppContext';

const Images = ({ programId }) => { // Accept programId as prop
  const { t } = useAppContext();
  const [viewMode, setViewMode] = useState('grid');
  const [imageData, setImageData] = useState([]);
  const [imageCategories, setImageCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isZipping, setIsZipping] = useState(false);

  const isExpired = (expiredDate) => {
    if (!expiredDate) return false;
    const expiry = new Date(expiredDate);
    if (expiry < new Date()) {
      return `(expired: ${expiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})`;
    }
    return false;
  };
  
  // const { selectedResult } = useAppContext(); // Removed context usage

  // Function to fetch image categories (defined only once)
  const fetchImageCategories = useCallback(async (tmsId) => { // Wrapped in useCallback
    if (!tmsId) return; // Exit if no tmsId
    setIsCategoryLoading(true);
    setError(null);
    setImageCategories([]); // Reset categories before fetching
    try {
      const data = await apiService.getImages(tmsId);

      // Extract unique categories from the image data
      if (data && data.length > 0) {
        const categories = [...new Set(data.map(img => img.category).filter(Boolean))];
        setImageCategories(categories);
      } else {
        setImageCategories([]);
      }
    } catch (err) {
      console.error('Failed to fetch image categories:', err);
      setError(t('programImages.errorCategories'));
      setImageCategories([]);
    } finally {
      setIsCategoryLoading(false);
    }
  }, []); // Empty dependency array

  // Fetch image categories when component mounts or when programId changes
  useEffect(() => {
    fetchImageCategories(programId);
  }, [programId, fetchImageCategories]);

  // Function to fetch images for a specific category (wrapped in useCallback)
  const fetchImagesByCategory = useCallback(async (tmsId, category) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getImages(tmsId);

      // Filter images by the selected category
      const filteredImages = data.filter(img => img.category === category);
      setImageData(filteredImages || []);
    } catch (err) {
      console.error('Failed to fetch images:', err);
      setError(t('programImages.errorImages'));
      setImageData([]);
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array as it doesn't depend on component state/props directly

  // Handle category selection (wrapped in useCallback)
  const handleCategorySelect = useCallback((category) => {
    setActiveCategory(category);
    if (programId) { // Use programId prop
      fetchImagesByCategory(programId, category);
    }
  }, [programId, fetchImagesByCategory]); // Updated dependencies

  // Auto-select first category when categories are loaded
  useEffect(() => {
    if (imageCategories.length > 0 && !activeCategory) {
      handleCategorySelect(imageCategories[0]);
    }
  }, [imageCategories, activeCategory, handleCategorySelect]); // Keep dependencies

  // Function to handle image click (defined only once)
  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleImageSelect = (image) => {
    setSelectedImages(prevSelected => {
      if (prevSelected.find(i => i.assetId === image.assetId)) {
        return prevSelected.filter(i => i.assetId !== image.assetId);
      } else {
        return [...prevSelected, image];
      }
    });
  };

  const handleDownloadZip = async () => {
    setIsZipping(true);
    const zip = new JSZip();
    const promises = selectedImages.map(async (image) => {
      const imageUrl = image.image_url.split('?')[0];
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const filename = imageUrl.split('/').pop();
      zip.file(filename, blob);
    });

    await Promise.all(promises);
    zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, `${programId}-selectedimages.zip`);
      setIsZipping(false);
      setIsModalOpen(false);
    });
  };

  const handleDownloadAllZip = async () => {
    setIsZipping(true);
    try {
      const allImages = await apiService.getImages(programId);
      const zip = new JSZip();
      const promises = allImages.map(async (image) => {
        const imageUrl = image.image_url.split('?')[0];
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const filename = imageUrl.split('/').pop();
        zip.file(filename, blob);
      });

      await Promise.all(promises);
      zip.generateAsync({ type: 'blob' }).then((content) => {
        saveAs(content, `${programId}-allimages.zip`);
        setIsZipping(false);
      });
    } catch (err) {
      console.error('Failed to fetch all images for zipping:', err);
      setError(t('programImages.errorDownload'));
      setIsZipping(false);
    }
  };

  // Render loading state for entire component
  if (isCategoryLoading && !imageCategories.length) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-8 w-8 text-blue-500 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600 text-sm">{t('programImages.loadingCategories')}</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && !imageCategories.length) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-red-500 text-center">
          <p className="mb-2">{error}</p>
          <button 
            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
            onClick={() => fetchImageCategories(programId)} // Use programId
          >
            {t('programTitles.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  // Render empty state for no categories
  if (!imageCategories || imageCategories.length === 0) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-gray-500 text-center">
          <p>{t('programImages.noImages')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-2 h-full overflow-auto">
      {/* Category selection bar - Always visible */}
      <div className="flex justify-between items-center mb-4 pb-2 border-b">
        <div className="flex flex-wrap gap-2">
          {imageCategories.map((category, index) => (
            <button
              key={index}
              onClick={() => handleCategorySelect(category)}
              className={`px-3 py-1 text-sm rounded border transition-colors ${
                activeCategory === category 
                  ? 'bg-blue-100 border-blue-300 text-blue-700' 
                  : 'bg-gray-100 border-gray-200 hover:bg-blue-50 hover:border-blue-200 text-gray-700'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
        
        <div className="flex items-center">
          {selectedImages.length > 0 && (
            <button
              className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm mr-2"
              onClick={() => setIsModalOpen(true)}
            >
              {isZipping ? t('programImages.zipping') : t('programImages.downloadAsZip')}
            </button>
          )}
          {imageData.length > 0 && (
            <button
              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm mr-4"
              onClick={handleDownloadAllZip}
              disabled={isZipping}
            >
              {isZipping ? t('programImages.zippingAll') : t('programImages.downloadAllAsZip')}
            </button>
          )}
          <button 
            className="px-2 py-1 border border-gray-300 rounded-l flex items-center text-xs"
            onClick={() => setViewMode('grid')}
          >
            <Grid size={14} className={`mr-1 ${viewMode === 'grid' ? 'text-blue-600' : 'text-gray-600'}`} />
            <span className={viewMode === 'grid' ? 'text-blue-600' : 'text-gray-600'}>{t('programImages.view.grid')}</span>
          </button>
          <button 
            className="px-2 py-1 border border-gray-300 border-l-0 rounded-r flex items-center text-xs"
            onClick={() => setViewMode('list')}
          >
            <List size={14} className={`mr-1 ${viewMode === 'list' ? 'text-blue-600' : 'text-gray-600'}`} />
            <span className={viewMode === 'list' ? 'text-blue-600' : 'text-gray-600'}>{t('programImages.view.list')}</span>
          </button>
        </div>
      </div>
      
      {/* Loading state for images */}
      {isLoading ? (
        <div className="w-full p-8 flex justify-center items-center">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-8 w-8 text-blue-500 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600 text-sm">{t('programImages.loading', { category: activeCategory })}</span>
          </div>
        </div>
      ) : error ? (
        <div className="w-full p-8 flex justify-center items-center">
          <div className="text-red-500 text-center">
            <p className="mb-2">{error}</p>
            <button 
              className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
              onClick={() => {
                if (programId && activeCategory) { // Use programId
                  fetchImagesByCategory(programId, activeCategory);
                }
              }}
            >
              {t('programTitles.tryAgain')}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Grid view */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {imageData.map((image, index) => {
                const expired = isExpired(image.expiredDate);
                return (
                <div
                  key={image.assetId || index}
                  className={`relative border rounded-md overflow-hidden transition-colors ${selectedImages.find(i => i.assetId === image.assetId) ? 'border-blue-500' : 'border-gray-100'} ${expired ? 'bg-yellow-100' : 'bg-gray-50'}`}
                >
                  <input
                    type="checkbox"
                    className="absolute top-2 left-2 z-10 h-5 w-5"
                    checked={selectedImages.some(i => i.assetId === image.assetId)}
                    onChange={() => handleImageSelect(image)}
                  />
                  <div 
                    className="aspect-video bg-gray-200 flex items-center justify-center cursor-pointer"
                    onClick={() => handleImageClick(image)}
                  >
                    {image.image_url ? (
                      <img
                        src={image.image_url.replace('w=240', 'w=400')} // Request larger image
                        alt={image.caption || `${image.category} image`}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          if (!e.target.dataset.failed) {
                            e.target.dataset.failed = '1';
                            e.target.src = '/offline.png';
                          }
                        }}
                      />
                    ) : (
                      <div className="text-3xl font-bold text-blue-300">
                        {image.category ? image.category[0] : 'I'}
                      </div>
                    )}
                  </div>
                  <div className="p-2">
                    <div className="text-xs font-medium">{image.category || 'Unknown'} {expired && <span className="text-red-500">{expired}</span>}</div>
                    <div className="text-xs text-gray-500">{image.assetId || 'Unknown'}</div>
                    {image.width && image.height && (
                      <div className="text-xs text-gray-500">
                        {image.width} x {image.height} ({image.ratio || 'Unknown'})
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium text-gray-600">{t('programImages.columns.select')}</th>
                  <th className="px-4 py-2 font-medium text-gray-600">{t('programImages.columns.preview')}</th>
                  <th className="px-4 py-2 font-medium text-gray-600">{t('programImages.columns.category')}</th>
                  <th className="px-4 py-2 font-medium text-gray-600">{t('programImages.columns.assetId')}</th>
                  <th className="px-4 py-2 font-medium text-gray-600">{t('programImages.columns.resolution')}</th>
                  <th className="px-4 py-2 font-medium text-gray-600">{t('programImages.columns.ratio')}</th>
                </tr>
              </thead>
              <tbody>
                {imageData.map((image, index) => {
                  const expired = isExpired(image.expiredDate);
                  return (
                  <tr
                    key={image.assetId || index}
                    className={`border-t border-gray-200 hover:bg-blue-50 cursor-pointer ${selectedImages.find(i => i.assetId === image.assetId) ? 'bg-blue-100' : ''} ${expired ? 'bg-yellow-100' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        className="h-5 w-5"
                        checked={selectedImages.some(i => i.assetId === image.assetId)}
                        onChange={() => handleImageSelect(image)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-4 py-3" onClick={() => handleImageClick(image)}>
                      <div className="w-16 h-9 bg-gray-200 flex items-center justify-center overflow-hidden">
                        {image.image_url ? (
                          <img
                            src={image.image_url}
                            alt={image.caption || `${image.category} image`}
                            className="max-w-full max-h-full object-contain"
                          />
                        ) : (
                          <span className="text-blue-300">{t('programImages.noImage')}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-blue-600">{image.category || 'Unknown'} {expired && <span className="text-red-500">{expired}</span>}</td>
                    <td className="px-4 py-3">{image.assetId || 'Unknown'}</td>
                    <td className="px-4 py-3">{image.width && image.height ? `${image.width} x ${image.height}` : 'Unknown'}</td>
                    <td className="px-4 py-3">{image.ratio || 'Unknown'}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Status bar */}
          {imageData.length > 0 && (
            <div className="mt-4 flex justify-between text-xs text-gray-500">
              <div>
                {t('programImages.showing', { count: imageData.length, category: activeCategory })}
              </div>
              <div>
                <button
                  className={`px-3 py-1 border border-gray-300 rounded-sm ${imageData.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                  onClick={() => exportToCsv(imageData, `${programId}_${activeCategory}_images.csv`)}
                  disabled={imageData.length === 0}
                >
                  {t('programImages.downloadCategory', { category: activeCategory })}
                </button>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Image Popup Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" 
          onClick={() => setSelectedImage(null)}
          style={{ zIndex: 9999 }}
        >
          <div className="relative bg-white rounded-lg overflow-hidden max-w-4xl max-h-screen" onClick={e => e.stopPropagation()}>
            <button 
              className="absolute top-2 right-2 bg-white rounded-full p-1 text-gray-800 hover:text-red-500 z-10"
              onClick={() => setSelectedImage(null)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-auto p-1 bg-gray-100 flex items-center justify-center">
                <img 
                  src={selectedImage.image_url ? selectedImage.image_url.split('?')[0] : ''}
                  alt={selectedImage.caption || `${selectedImage.category} image`}
                  className="max-w-full max-h-[70vh] object-contain"
                  onError={(e) => {
                    if (!e.target.dataset.failed) {
                      e.target.dataset.failed = '1';
                      e.target.src = '/offline.png';
                    }
                  }}
                />
              </div>
              
              <div className="p-4 border-t border-gray-200">
                <div className="font-medium">{selectedImage.category || 'Unknown'}</div>
                <div className="text-sm text-gray-600">{t('programImages.columns.assetId')}: {selectedImage.assetId || 'Unknown'}</div>
                {selectedImage.width && selectedImage.height && (
                  <div className="text-sm text-gray-600">
                    {t('programImages.columns.resolution')}: {selectedImage.width} x {selectedImage.height} ({selectedImage.ratio || 'Unknown'})
                  </div>
                )}
                {selectedImage.caption && (
                  <div className="text-sm text-gray-600 mt-2">
                    <span className="font-medium">{t('programImages.caption')}</span> {selectedImage.caption}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" 
          onClick={() => setIsModalOpen(false)}
          style={{ zIndex: 9999 }}
        >
          <div className="relative bg-white rounded-lg overflow-hidden max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b">
              <h2 className="text-lg font-medium">{t('programImages.modal.title')}</h2>
            </div>
            <div className="p-4 max-h-[70vh] overflow-auto">
              <p>{t('programImages.modal.text', { count: selectedImages.length })}</p>
              <ul className="list-disc list-inside mt-4">
                {selectedImages.map(image => (
                  <li key={image.assetId} className="text-sm">
                    {image.image_url.split('?')[0].split('/').pop()}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                onClick={() => setIsModalOpen(false)}
              >
                {t('programImages.modal.cancel')}
              </button>
              <button
                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handleDownloadZip}
                disabled={isZipping}
              >
                {isZipping ? t('programImages.zipping') : t('programImages.modal.download')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Images;
